import React from 'react';
import CardCommon from '@op/components/CardCommon';
import { StatusTip } from 'tea-component';
import { useScrollBottomRegister } from '@opm/components/ScrollBottomList';
import type { TGameMap } from '@opm/pages/CompetitorAdComparison/hooks/useGameMap';
import { ITrendItemBase, ITopCreative, IListMax, trendKey } from '../types';
import { TTrendCardKPIConfigItem } from '../constants';
import type { TTopicTypeColorMap } from '../utils';
import TrendCard from './TrendCard';
import './index.scss';

export interface ICardViewProps {
  data: ITrendItemBase[];
  /** 当前 tab + filter 下全量 KPI 最大值（list.md `max` 字段，进度条归一化用） */
  listMax?: IListMax;
  /** 游戏元信息字典（cond.materials_config_games）：id → meta */
  gameMap?: TGameMap;
  /** Topic Type chip 颜色映射（cond.creative_trends_config_topic_types）：L1.label_en → color */
  topicTypeColorMap?: TTopicTypeColorMap;
  isFirstLoading?: boolean;
  isEmpty?: boolean;
  isPaginationLoading?: boolean;
  nextPage: () => void;
  onCardClick?: (record: ITrendItemBase) => void;
  onTitleClick?: (record: ITrendItemBase) => void;
  onThumbnailClick?: (item: ITopCreative, record: ITrendItemBase) => void;
  /** 选中项 key，由 trendKey(record) 生成 */
  selectedTrendKey?: string;
  filter: any;
  kpiConfigs?: TTrendCardKPIConfigItem[];
  thumbnailsAccessor?: (record: ITrendItemBase) => any[] | undefined;
  renderThumbnail?: (args: {
    item: any;
    record: ITrendItemBase;
    index: number;
    filter: any;
    onThumbnailClick?: (item: ITopCreative, record: ITrendItemBase) => void;
  }) => React.ReactNode;
}

/**
 * CardView 卡片网格视图（按 Figma 3902_52416 设计 + 滚动触底加载）
 *
 * Grid 响应式列数：
 * - 默认: 2 列（卡片设计宽 561px）
 * - < 1366px 或 Drawer 打开后空间不足: 1 列
 */
export default function CardView({
  data,
  listMax,
  gameMap,
  topicTypeColorMap,
  isFirstLoading,
  isEmpty,
  isPaginationLoading,
  nextPage,
  onCardClick,
  onTitleClick,
  onThumbnailClick,
  selectedTrendKey,
  filter,
  kpiConfigs,
  thumbnailsAccessor,
  renderThumbnail,
}: ICardViewProps) {
  // 注册滚动触底回调
  useScrollBottomRegister(nextPage);

  return (
    <CardCommon
      loading={isFirstLoading}
      empty={isEmpty}
      emptyHeight={615}
      loadingHeight={615}
      contentCard
    >
      <div className="dbom-ct-card-grid">
        {data?.map((item) => {
          const key = trendKey(item);
          return (
            <TrendCard
              key={key}
              data={item}
              listMax={listMax}
              gameMap={gameMap}
              topicTypeColorMap={topicTypeColorMap}
              onClick={onCardClick}
              onTitleClick={onTitleClick}
              onThumbnailClick={onThumbnailClick}
              selected={key === selectedTrendKey}
              filter={filter}
              kpiConfigs={kpiConfigs}
              thumbnailsAccessor={thumbnailsAccessor}
              renderThumbnail={renderThumbnail}
            />
          );
        })}
      </div>
      {isPaginationLoading && (
        <div className="loading-common dbom-loading-common">
          <StatusTip status="loading" />
        </div>
      )}
    </CardCommon>
  );
}
